import { Component, type ErrorInfo, type ReactNode } from 'react';

import i18n from '@/i18n';
import { captureException } from '@/lib/sentryLazy';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * T2899 — meja napak aplikacije brez Sentryja v vstopnem grafu.
 *
 * Do te naloge je bil `App` ovit v `Sentry.withErrorBoundary`, kar je ves
 * `@sentry/react` povleklo v začetni prenos. Ta meja izriše isti nadomestni
 * zaslon (`app.error_boundary_title`) in napako poroča prek `sentryLazy`: če
 * Sentry še ni naložen, gre v vrsto in se pošlje ob `init`.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
      tags: { error_boundary: 'app' },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen text-muted-foreground text-body">
          {i18n.t('app.error_boundary_title')}
        </div>
      );
    }
    return this.props.children;
  }
}
